import { useState, useCallback, useEffect } from 'react';

const SIDEBAR_STORAGE_KEY = 'admin_sidebar_collapsed';
/** Matches Tailwind `md` — below this, sidebar becomes an off-canvas drawer */
const MOBILE_MQ = '(max-width: 767px)';

function getIsMobile(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_MQ).matches;
}

export function useSidebar() {
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(getIsMobile);

    useEffect(() => {
        const mql = window.matchMedia(MOBILE_MQ);
        const onChange = () => {
            const mobile = mql.matches;
            setIsMobile(mobile);
            if (!mobile) setMobileOpen(false);
        };
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (!mobileOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mobileOpen]);

    const toggle = useCallback(() => {
        setCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next)); } catch { /* ignore */ }
            return next;
        });
    }, []);

    const openMobile = useCallback(() => setMobileOpen(true), []);
    const closeMobile = useCallback(() => setMobileOpen(false), []);
    const toggleMobile = useCallback(() => setMobileOpen(prev => !prev), []);

    return {
        collapsed,
        toggle,
        isMobile,
        mobileOpen,
        openMobile,
        closeMobile,
        toggleMobile,
    };
}
